import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button } from 'antd';

import useDocsData from '../docs/useDocsData';

// Per-viewer memory of a dismissed nudge. A convenience, so every read and
// write is guarded: a private window or blocked storage simply shows the
// nudge again, it never breaks the surface.
const STORAGE_PREFIX = 'keepr.guidance.dismissed.';

function wasDismissed(key) {
	if (!key) return false;
	try { return window.localStorage.getItem(STORAGE_PREFIX + key) === '1'; } catch { return false; }
}

function rememberDismissed(key) {
	if (!key) return;
	try { window.localStorage.setItem(STORAGE_PREFIX + key, '1'); } catch { /* convenience only */ }
}

const TONE_TYPES = { suggest: 'info', info: 'info', caution: 'warning' };

// A guide the index does not know (not built yet, or renamed) still gets a
// readable link rather than a raw slug.
function fallbackTitle(id) {
	const slug = String(id || '').split('/').pop() || '';
	const words = slug.replace(/-/g, ' ').trim();
	return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Read the guide';
}

/**
 * THE guidance nudge (docs/plans/personal-discover-global.md §2.4): a calm
 * callout that helps a person make the technically sound choice without
 * refusing the other one — "sharing works here, but a collection built for
 * it is easier to keep tidy".
 *
 *   <Guidance
 *     title="This is your personal collection"
 *     why="Sharing works, but a collection built for …"
 *     links={['collections/create-a-collection', 'sharing-and-export/share-with-a-person']}
 *     action={{ label: 'Create a collection', to: '/collections/new' }}
 *     tone="suggest"
 *     dismissKey="personal-sharing"
 *   />
 *
 * `why` is one or two sentences — or an array of them, each its own line,
 * for a nudge that has several separate things to say (the list's warning
 * about a filter naming what is gone says one per condition).
 *
 * `links` are guide ids (`<section>/<slug>`); their titles come from the
 * public guide index, so a renamed guide renames every nudge that points at
 * it. At most three are shown. `action` is `{ label, to }` or
 * `{ label, onClick }` and renders as a link-style button in `--action` —
 * never a second filled primary. `tone` picks the Alert type (`suggest` and
 * `info` are informational, `caution` a warning). `dismissKey` makes it
 * closable and remembers the close per viewer.
 *
 * A `below`-row element: it sits under the thing it advises on, never
 * inside a badge. On a phone the links and the action wrap onto their own
 * lines (guidance.css).
 */
export default function Guidance({
	title,
	why = null,
	links = [],
	action = null,
	tone = 'suggest',
	dismissKey = null,
	className = '',
}) {
	const navigate = useNavigate();
	const [dismissed, setDismissed] = useState(() => wasDismissed(dismissKey));
	const wanted = (Array.isArray(links) ? links : []).filter(Boolean).slice(0, 3);
	const { data: index } = useDocsData('guides', { enabled: wanted.length > 0 });

	if (dismissed) return null;

	const titleOf = id => {
		const guide = Array.isArray(index?.guides) ? index.guides.find(entry => entry.id === id) : null;
		return guide?.title || fallbackTitle(id);
	};

	const actionNode = action && action.label ? (
		<Button
			type="link"
			className="guidance__action"
			onClick={() => {
				if (typeof action.onClick === 'function') action.onClick();
				else if (action.to) navigate(action.to);
			}}
		>
			{action.label}
		</Button>
	) : null;

	const whys = (Array.isArray(why) ? why : [why]).filter(Boolean);
	const description = (whys.length || wanted.length || actionNode) ? (
		<div className="guidance__body">
			{whys.map((line, index) => <p key={index} className="guidance__why">{line}</p>)}
			{(wanted.length || actionNode) ? (
				<div className="guidance__links">
					{actionNode}
					{wanted.map(id => (
						<Link key={id} to={`/docs/guides/${id}`} className="guidance__link">{titleOf(id)}</Link>
					))}
				</div>
			) : null}
		</div>
	) : null;

	return (
		<Alert
			type={TONE_TYPES[tone] || 'info'}
			showIcon
			className={['guidance', className].filter(Boolean).join(' ')}
			message={title}
			description={description}
			closable={Boolean(dismissKey)}
			onClose={() => {
				rememberDismissed(dismissKey);
				setDismissed(true);
			}}
		/>
	);
}
